import debounce from 'just-debounce-it'
import {useCallback, useContext, useEffect, useRef, useState} from 'react'
import {Col, Dropdown, Form, Row, Stack} from 'react-bootstrap'
import {useLocation, useSearchParams} from 'react-router-dom'
import Select, {SelectInstance, components} from 'react-select'
import {KTIcon} from '../../../../../_metronic/helpers'
import {useUserInfo} from '../../../../routing/store/InfoUserProvider'
import {fichasxid, generateExcel, generateSimplifiedExcel} from '../../../../services/fichas'
import {fetchMenuActionsByRole, fetchSubMenuByUrl} from '../../../../services/menu'
import {useGlobalOverlayLoading} from '../../../../shared/hooks/useGlobalOverlayLoading'
import {useAuth2} from '../../../authv2/Auth2Provider'
import {DatePickerBusiness} from '../../../generales_component/DatePickerBusiness'
import {PartnerContext} from '../PartnerContexts'

const customStyles = {
  option: (provided: any, state: any) => ({
    ...provided,
    minHeight: '20px',
    height: '34.5px',
    backgroundColor: state.isSelected ? 'transparent' : provided.backgroundColor,
    color: state.isSelected ? 'inherit' : provided.color,
  }),
  multiValue: (provided: any) => ({
    ...provided,
    backgroundColor: 'transparent',
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    color: 'inherit',
  }),
  multiValueRemove: (provided: any) => ({
    ...provided,
    color: 'inherit',
    ':hover': {
      backgroundColor: 'transparent',
      color: 'inherit',
    },
  }),
}

const CustomOption = (props: any) => {
  return (
    <components.Option {...props} className='d-flex align-items-center gap-2'>
      <input type='checkbox' checked={props.isSelected} onChange={() => null} />
      <label>{props.data.label}</label>
    </components.Option>
  )
}

const CustomValueContainer = ({children, ...props}: any) => {
  const length = props.getValue().length

  if (length > 1) {
    return <div className='value-container mx-2'>{length} opciones seleccionadas</div>
  }

  return (
    <div className='value-container mx-2'>
      {children[0] !== null && children[0]?.key === 'placeholder'
        ? 'Seleccione'
        : children[0]?.key !== 'placeholder'
        ? children[0]
        : ''}
      {children[1]}
    </div>
  )
}

const allStates = [
  // {
  //   value: 'all',
  //   label: 'Todos',
  //   state: 'process',
  // },
  {
    value: 'pendientePorColaborador',
    label: 'Fichas Pendientes',
    state: 'sheet',
  },
  {
    value: 'fichaSiendoEditada',
    label: 'Fichas en Progreso',
    state: 'sheet',
  },
  {
    value: 'fichaObservada',
    label: 'Fichas Observadas',
    state: 'sheet',
  },
  {
    value: 'completadoPorColaborador',
    label: 'Fichas Completadas',
    state: 'sheet',
  },
  {
    value: 'aprobadoConPendiente',
    label: 'Fichas Aprobadas con Pendientes',
    state: 'sheet',
  },
  {
    value: 'aprobadoPorAdmin',
    label: 'Fichas Aprobadas',
    state: 'sheet',
  },
  {
    value: 'historico',
    label: 'Fichas Historicas',
    state: 'sheet',
  },
  {
    value: 'documentosPendientesDeFirma', // before "pending"
    label: 'Documentos Pendientes de Firma',
    state: 'sheet', // before "document"
  },
  {
    value: 'documentosEnProcesoDeFirma', // before "viewed"
    label: 'Documentos en Proceso de Firma',
    state: 'sheet', // before "document"
  },
  {
    value: 'documentosFirmados', // before "signed"
    label: 'Documentos Firmados',
    state: 'sheet', // before "document"
  },
  {
    value: 'procesoCerrado',
    label: 'Procesos Cerrados',
    state: 'process',
  },
  {
    value: 'procesoCerradoConPendiente',
    label: 'Procesos Cerrados con Pendientes',
    state: 'process',
  },
  {
    value: 'procesoAnulado',
    label: 'Procesos Anulados',
    state: 'process',
  },
]

const allStatesRegular = [
  {
    value: 'documentosPendientesDeFirma', // before "pending"
    label: 'Documentos Pendientes de Firma',
    state: 'sheet', // before "document"
  },
  {
    value: 'documentosEnProcesoDeFirma', // before "viewed"
    label: 'Documentos en Proceso de Firma',
    state: 'sheet', // before "document"
  },
  {
    value: 'documentosFirmados', // before "signed"
    label: 'Documentos Firmados',
    state: 'sheet', // before "document"
  },
  {
    value: 'procesoCerrado',
    label: 'Procesos Cerrados',
    state: 'process',
  },
  {
    value: 'procesoCerradoConPendiente',
    label: 'Procesos Cerrados con Pendientes',
    state: 'process',
  },
  {
    value: 'procesoAnulado',
    label: 'Procesos Anulados',
    state: 'process',
  },
]

interface Campo {
  _id: string
  titulo: string
  tabla: any
  idtipo: Record<string, string>
  opciones?: {id: string; name: string; parent_id: string}[]
}

interface Filtro {
  idseccion: string
  campos: Campo[]
}

interface FiltroState {
  titulo: string
  tipo: Record<string, string>
  opciones?: {id: string; name: string; parent_id: string}[]
}

interface FiltroProceso {
  idficha: string
  idproceso: string
  filtros: Filtro[]
}

const PartnersListHeader = () => {
  const {unidadesAdmin, procesosAdmin, unidadesGrouped} = useUserInfo()

  const [idproceso, setIdproceso] = useState('')
  const [idunidad, setIdunidad] = useState('')
  const [idfichas, setIdfichas] = useState('')
  const [filtros, setFiltros] = useState<FiltroProceso[]>([])
  const [options, setOptions] = useState<'equal' | 'distinct'>('equal')
  const [selectedOption, setSelectedOption] = useState('')
  const [filtro, setFiltro] = useState<FiltroState | null>(null)
  const selectRef = useRef<SelectInstance>(null)
  const allStatesRef = useRef<SelectInstance>(null)
  const [selectedOptions, setSelectedOptions] = useState<any[]>([])
  const [searchParams] = useSearchParams()
  const [proccessRegular, setProccessRegular] = useState<boolean>(false)
  const [newFiltro, setNewFiltro] = useState<{[key: string]: string}>({})

  const {
    activeOnly,
    setActiveOnly,
    state,
    setState,
    searchTerm,
    setSearchTerm,
    setSheetId,
    entryDateFrom,
    entryDateTo,
    openFichaPlantillaModal,
    setTypeUpdate,
    setShowCargaExcelModal,
    setShowCargaEdicionExcelModal,
    setProcessId,
    processId,
    setUnidadId,
    unidadId,
    setQuery,
    setFiltrosKeys,
    setFiltroData,
    filtroData,
    setCampoUnidad,
    setFichaData,
    Dates,
    setDates,
    documentState,
    setDocumentState,
    hints,
    setCurrentPage,
  } = useContext(PartnerContext)

  const {setGlobalOverlayLoading} = useGlobalOverlayLoading()
  const location = useLocation()
  const {selectedRole} = useAuth2()
  const [permissions, setPermissions] = useState<string[]>([])

  useEffect(() => {
    const fetchPermissions = async () => {
      const response = await fetchMenuActionsByRole(selectedRole)
      if (response.success) {
        const currentPath = location.pathname
        const subMenuResponse = await fetchSubMenuByUrl(currentPath)
        if (subMenuResponse.success) {
          const subMenuId = subMenuResponse.data.id_hijo
          const permissionsForSubMenu =
            response.data.find((item: any) => item.subMenuId === subMenuId)?.acciones || []
          setPermissions(permissionsForSubMenu)
        }
      }
    }

    fetchPermissions()
  }, [selectedRole])

  useEffect(() => {
    const dataFiltro: any = localStorage.getItem('filtroHistorico') || '{}'
    const dataFiltroHistorico = JSON.parse(dataFiltro)
    //Agregar Filtro Estado Guardado
    if (dataFiltroHistorico?.states) {
      const estados = dataFiltroHistorico.states
      const groupedStates = estados.reduce(
        (acc: any, option: any) => {
          if (acc[option.state]) {
            acc[option.state] = acc[option.state].concat(option.value)
          } else {
            acc[option.state] = [option.value]
          }

          return acc
        },
        {sheet: [], document: [], process: []}
      )
      setActiveOnly(groupedStates.process.length > 0 ? groupedStates.process.join(',') : 'all')
      setDocumentState(groupedStates.document.length > 0 ? groupedStates.document.join(',') : 'all')
      setState(groupedStates.sheet.length > 0 ? groupedStates.sheet.join(',') : '')
      setSelectedOptions(estados)
    }
  }, [])

  useEffect(() => {
    const dataFiltro: any = localStorage.getItem('filtroHistorico') || '{}'
    const dataFiltroHistorico = JSON.parse(dataFiltro)

    const firstProcess = procesosAdmin?.[0]?.id ?? ''

    if (!searchParams.get('idproceso')) {
      if (dataFiltroHistorico?.idProceso) {
        setIdproceso(dataFiltroHistorico.idProceso)
        setProcessId(dataFiltroHistorico.idProceso)
      } else {
        const processDefault = procesosAdmin.length > 1 ? 'all' : procesosAdmin?.[0]?.id ?? 'all'
        setIdproceso(processDefault)
        setProcessId(processDefault)
      }
    }

    if (!searchParams.get('unidad')) {
      if (dataFiltroHistorico?.codUnidad) {
        setIdunidad(dataFiltroHistorico.codUnidad)
        setUnidadId(dataFiltroHistorico.codUnidad)
      } else {
        const unidadesByProcess = unidadesGrouped[firstProcess] ?? []
        const unidadDefault = unidadesByProcess?.length > 1 ? 'all' : unidadesByProcess?.[0]?.id
        setIdunidad(unidadDefault ?? '')
        setUnidadId(unidadDefault ?? '')
      }
    }
  }, [unidadesAdmin, unidadesGrouped, procesosAdmin])

  // useEffect(() => {
  //   if (idproceso !== '') {
  //     getFichasxProceso(idproceso)
  //   }
  // }, [idproceso])

  useEffect(() => {
    const filtrosByFicha = filtros.filter(
      (filtro) => filtro.idficha === idfichas && filtro.idproceso === idproceso
    )

    const misFiltros =
      filtrosByFicha.length > 0 ? filtrosByFicha[0].filtros[0].campos : ([] as Campo[])

    const filtrosKeys = misFiltros.map((f) => f.titulo.toLowerCase())

    const filtroData = misFiltros.map((f) => ({
      campo: f.titulo.toLowerCase(),
      isUnidad: f.tabla?.toLowerCase() === 'unidades' && f.idtipo.nombre === 'Lista desplegable BD',
      values:
        f.tabla !== 'Unidades' && f.idtipo.nombre === 'Lista desplegable BD'
          ? f.opciones
          : undefined,
    }))

    const campoUnidad = misFiltros.find((f) => f.tabla === 'Unidades') ?? null

    setCampoUnidad(campoUnidad?.titulo ?? '')
    setFiltrosKeys(filtrosKeys)
    setFiltroData(filtroData)
    setFiltro(
      misFiltros[0] != null
        ? {
            titulo: misFiltros[0].titulo,
            tipo: misFiltros[0].idtipo,
            opciones: misFiltros[0].opciones,
          }
        : null
    )
  }, [filtros])

  useEffect(() => {
    if (filtro != null && options != null) {
      setQuery({
        match: options,
        campo: filtro.titulo,
        values: selectedOption,
      })
    }
  }, [filtro, options, selectedOption])

  useEffect(() => {
    if (Object.keys(newFiltro).length > 0) {
      const newdataFiltro = localStorage.getItem('filtroHistorico') || '{}'
      const newdataFiltroHistorico = JSON.parse(newdataFiltro)
      localStorage.setItem(
        'filtroHistorico',
        JSON.stringify({...newdataFiltroHistorico, ...newFiltro})
      )
    }
  }, [newFiltro])

  useEffect(() => {
    if (searchParams.get('idproceso')) {
      setIdproceso(searchParams.get('idproceso') ?? '')
      setProcessId(searchParams.get('idproceso') ?? '')
    }

    if (searchParams.get('unidad')) {
      setUnidadId(searchParams.get('unidad') ?? '')
      setIdunidad(searchParams.get('unidad') ?? '')
    }

    if (searchParams.get('dni')) {
      setSearchTerm(searchParams.get('dni') ?? '')
    }
  }, [searchParams])

  const getFichasxProceso = (value: string) => {
    fichasxid(value).then((response) => {
      if (response.success) {
        setIdfichas(response.data[0]?.idficha?._id)
        setSheetId(response.data[0]?.idficha?._id)
        setFichaData(
          response.data.map((x: any) => ({label: x.idficha?.nombre, value: x.idficha?._id}))
        )
        setFiltros(response.procesoFiltros ?? [])
      } else {
        console.log(response.msg)
      }
    })
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setNewFiltro({search: value})
    setCurrentPage(1)
  }

  const handleInputChange = (e: any) => {
    const selectedValues = e.map((x: any) => x.value)
    let finalSelected: any[] = []

    if (selectedValues.length === 1 && selectedValues[0] === 'all') {
      finalSelected = e.filter((x: any) => x.value === 'all')
    } else if (selectedValues.includes('all') && selectedValues.length > 1) {
      const lastSelected = e[e.length - 1]
      if (lastSelected.value === 'all') {
        finalSelected = [lastSelected]
      } else {
        finalSelected = [lastSelected]
      }
    } else if (!selectedValues.includes('all')) {
      if (selectedValues.length === 0) {
        finalSelected = [{value: 'all', label: 'Todos'}]
      } else {
        finalSelected = e
      }
    }

    const valuesFinal = finalSelected.map((x: any) => x.value).join(',')
    setIdproceso(valuesFinal)
    setProcessId(valuesFinal)

    const hasMoreProcess = valuesFinal.length > 1 || valuesFinal.includes('all')
    const unidadesByProcess = hasMoreProcess ? unidadesAdmin : unidadesGrouped[valuesFinal?.[0]] ?? []
    const moreUnids = unidadesByProcess?.length > 1
    const unidadValue = moreUnids ? 'all' : unidadesByProcess?.[0]?.id ?? ''
    setIdunidad(unidadValue)
    setUnidadId(unidadValue)

    setQuery(null)
    setSelectedOption('')
    selectRef.current?.clearValue()
    setProccessRegular(
      hasMoreProcess ? false : procesosAdmin.filter((proc) => proc.id === selectedValues?.[0])?.[0]?.isRegular ? true : false
    )
    setNewFiltro({idProceso: valuesFinal, codUnidad: unidadValue})
    setCurrentPage(1)
  }

  const handleInputChangeUnidad = (e: any) => {
    const selectedValues = e.map((x: any) => x.value)
    let finalSelected: any[] = []

    if (selectedValues.length === 1 && selectedValues[0] === 'all') {
      finalSelected = e.filter((x: any) => x.value === 'all')
    } else if (selectedValues.includes('all') && selectedValues.length > 1) {
      const lastSelected = e[e.length - 1]
      if (lastSelected.value === 'all') {
        finalSelected = [lastSelected]
      } else {
        finalSelected = [lastSelected]
      }
    } else if (!selectedValues.includes('all')) {
      finalSelected = e
    }

    const idsUnidad = finalSelected.map((x: any) => x.value).join(',')

    setIdunidad(idsUnidad)
    setUnidadId(idsUnidad)
    setQuery(null)
    setSelectedOption('')
    selectRef.current?.clearValue()
    setNewFiltro({codUnidad: idsUnidad})
    setCurrentPage(1)
  }

  const handleChange = (e: any) => {
    const groupedStates = e.reduce(
      (acc: any, option: any) => {
        if (acc[option.state]) {
          acc[option.state] = acc[option.state].concat(option.value)
        } else {
          acc[option.state] = [option.value]
        }

        return acc
      },
      {sheet: [], document: [], process: []}
    )

    setSelectedOptions(e || [])
    setActiveOnly(groupedStates.process.length > 0 ? groupedStates.process.join(',') : 'all')
    setDocumentState(groupedStates.document.length > 0 ? groupedStates.document.join(',') : 'all')
    setState(groupedStates.sheet.length > 0 ? groupedStates.sheet.join(',') : '')
    setNewFiltro({states: e || []})
    setCurrentPage(1)
  }

  const handleExport = (id: any) => {
    const params = {
      activeOnly: activeOnly,
      sheetStates: state,
      searchQuery: searchTerm,
      entryDateFrom: entryDateFrom,
      entryDateTo: entryDateTo,
      unidad: unidadId,
      processId: processId,
      documentState,
    }

    setGlobalOverlayLoading(true)
    generateExcel(params)
      .then((response) => console.log(response))
      .catch((error) => console.log(error))
      .finally(() => setGlobalOverlayLoading(false))
  }

  const handleSimplifiedExport = (id: any) => {
    setGlobalOverlayLoading(true)
    generateSimplifiedExcel()
      .then((response) => console.log(response))
      .catch((error) => console.log(error))
      .finally(() => setGlobalOverlayLoading(false))
  }

  const handleShowFicha = () => {
    setTypeUpdate(false)
    openFichaPlantillaModal()
  }

  const handleShowFichaEdicion = () => {
    setTypeUpdate(true)
    openFichaPlantillaModal()
  }

  const handleShowCargaExcel = () => {
    setShowCargaExcelModal(true)
  }

  const handleShowCargaEdicionExcel = () => {
    setShowCargaEdicionExcelModal(true)
  }

  const handleChangeOptions = (e: any) => {
    setOptions(e.target.value)
  }

  const handleChangeFilterType = (e: any) => {
    filtros.forEach((filtro) => {
      filtro.filtros.forEach((f) => {
        f.campos.forEach((c) => {
          if (c._id === e.target.value) {
            setFiltro({
              titulo: c.titulo,
              tipo: c.idtipo,
              opciones: c.opciones,
            })
            selectRef.current?.clearValue()
          }
        })
      })
    })
  }

  const handleChangeOption = (e: any) => {
    console.log(e)
    if (Array.isArray(e)) {
      setSelectedOption(e.map((e) => e.value).join('@'))
    } else {
      setSelectedOption(e.value)
    }
  }

  const onChageText = useCallback(
    debounce((value: string) => {
      setSelectedOption(value)
    }, 500),
    []
  )

  const handleDateChange = (e: any) => {
    setDates(e)
    setCurrentPage(1)
  }

  const filtrosByFicha = filtros.filter(
    (filtro) => filtro.idficha === idfichas && filtro.idproceso === idproceso
  )

  const misFiltros =
    filtrosByFicha.length > 0 ? filtrosByFicha[0].filtros[0].campos : ([] as Campo[])

  const optionsSelect =
    filtro != null && filtro.opciones != null
      ? filtroData.find((f) => f.campo === filtro.titulo.toLowerCase())?.isUnidad
        ? unidadesAdmin.map((u) => ({
            label: u.id,
            value: u.name,
          }))
        : filtroData
            .find((f) => f.campo === filtro.titulo.toLowerCase())
            ?.values?.map((o: Record<string, string>) => ({
              label: o.name,
              value: o.id,
            }))
      : []

  const procesosIds = idproceso.split(',')
  const hasAllOptions =
    (unidadesAdmin).length > 1 ? [{id: 'all', name: 'Todas'}] : []

  const hasMoreProcess = procesosIds.length > 1 || procesosIds.includes('all')
  const unidadesByProcess = hasAllOptions.concat(
    hasMoreProcess ? unidadesAdmin : unidadesGrouped[idproceso] ?? []
  )
  const unidadesByProcessOptions = unidadesByProcess.map((x) => ({
    label: x.name,
    value: x.id,
  }))

  const hasAllProcesosOptions =
    (procesosAdmin ?? []).length > 1 ? [{id: 'all', name: 'Todos'}] : []
  const procesosOptions = hasAllProcesosOptions.concat(procesosAdmin).map((x) => ({
    label: x.name,
    value: x.id,
  }))

  return (
    <>
      <div className='mx-8 mt-8'>
        <Row className='mb-4'>
          <Col>
            <Stack>
              <span className='fw-bold'>Proceso:</span>
              <Select
                isMulti
                value={
                  !idproceso
                    ? procesosOptions[0]
                    : procesosOptions.filter((p) => idproceso.split(',').includes(p.value))
                }
                defaultValue={procesosOptions.find((p) => p.value === 'all')}
                hideSelectedOptions={false}
                isClearable
                closeMenuOnSelect={false}
                onChange={(event) => handleInputChange(event)}
                options={procesosOptions}
              />
            </Stack>
          </Col>
          <Col>
            <Stack>
              <span className='fw-bold'>Unidad:</span>
              <Select
                isMulti
                value={
                  !idunidad
                    ? unidadesByProcessOptions[0]
                    : unidadesByProcessOptions.filter((u) => idunidad.split(',').includes(u.value))
                }
                defaultValue={unidadesByProcessOptions.find((u) => u.value === 'all')}
                isClearable
                hideSelectedOptions={false}
                closeMenuOnSelect={false}
                onChange={handleInputChangeUnidad}
                options={unidadesByProcessOptions}
              />
            </Stack>
          </Col>
          <Col>
            <Stack direction='vertical' className='h-100'>
              <span className='fw-bold'>Buscar</span>
              <Form.Control
                type='text'
                className={'form-control-sm  same-height'}
                placeholder='Busca por DNI | Nombres | Apellidos | Cargo'
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                // searchTermDef={searchParams.get('dni') ?? ''}
              />
            </Stack>
          </Col>
        </Row>

        <Row className=''>
          <Col>
            <Stack direction='vertical' className='h-100'>
              <span className='fw-bold'>Rangos de Fecha de ingreso</span>
              <DatePickerBusiness value={Dates} onChange={handleDateChange} />
            </Stack>
          </Col>
          <Col>
            <Row>
              <Col>
                <Stack direction='vertical' className='h-100'>
                  <span className='fw-bold'>Estado del proceso:</span>
                  <Select
                    options={proccessRegular ? allStatesRegular : allStates}
                    value={selectedOptions}
                    onChange={handleChange}
                    isMulti
                    components={{ValueContainer: CustomValueContainer, Option: CustomOption}}
                    placeholder='Seleccione estados'
                    hideSelectedOptions={false}
                    ref={allStatesRef}
                    isClearable
                    closeMenuOnSelect={false}
                    isOptionDisabled={(option: any) => option.value === 'all'}
                    styles={customStyles}
                  />
                </Stack>
              </Col>
              {misFiltros.length > 0 && (
                <>
                  <Col className='mt-3'>
                    <Dropdown autoClose={false} drop='end'>
                      <Dropdown.Toggle variant='primary' id='dropdown-autoclose-false'>
                        Filtros
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Stack direction='horizontal' gap={2}>
                          <Dropdown.Item></Dropdown.Item>
                        </Stack>

                        <Stack direction='horizontal' gap={2}>
                          <Dropdown.Item>
                            <span className='fw-bold'>Personalizado:</span>
                            <Form.Select onChange={handleChangeFilterType}>
                              {misFiltros.map((filtro) => (
                                <option key={filtro._id} value={filtro._id} label={filtro.titulo} />
                              ))}
                            </Form.Select>
                          </Dropdown.Item>

                          <Dropdown.Item>
                            <Form.Select onChange={handleChangeOptions}>
                              <option value='equal'>Es</option>
                              <option value='distinct'>No es</option>
                            </Form.Select>
                          </Dropdown.Item>
                        </Stack>

                        <Dropdown.Item>
                          {filtro != null && filtro?.tipo.nombre === 'Lista desplegable BD' ? (
                            <>
                              <Select
                                ref={selectRef}
                                onChange={handleChangeOption}
                                options={optionsSelect}
                                isMulti
                              />
                            </>
                          ) : (
                            <input
                              type='text'
                              className='form-control'
                              placeholder='Buscar'
                              onChange={(e) => {
                                onChageText(e.target.value)
                              }}
                            />
                          )}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                </>
              )}
            </Row>
          </Col>
          <Col>
            <Stack className='mt-4' direction='horizontal' gap={2}>
              <Stack direction='horizontal' gap={2}>
                <Dropdown>
                  {permissions.includes('Exportar') && (
                    <Dropdown.Toggle variant='primary' id='dropdown-basic'>
                      <KTIcon iconName='file-down' className='fs-2' />
                      Exportar
                    </Dropdown.Toggle>
                  )}
                  <Dropdown.Menu>
                    <Dropdown.Item href='#' onClick={handleSimplifiedExport}>
                      Reporte simple
                    </Dropdown.Item>
                    <Dropdown.Item href='#' onClick={handleExport}>
                      Reporte detallado
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Stack>

              <Stack direction='horizontal' gap={2}>
                <Dropdown>
                  {permissions.includes('Carga Masiva de Procesos') && (
                    <Dropdown.Toggle variant='primary' id='dropdown-basic'>
                      <KTIcon iconName='file-down' className='fs-2' />
                      Carga Masiva de Procesos
                    </Dropdown.Toggle>
                  )}
                  <Dropdown.Menu>
                    <Dropdown.Item href='#' onClick={handleShowFicha}>
                      Exportar Plantilla
                    </Dropdown.Item>
                    <Dropdown.Item href='#' onClick={handleShowCargaExcel}>
                      Cargar Plantilla de creación
                    </Dropdown.Item>
                    <Dropdown.Item href='#' onClick={handleShowFichaEdicion}>
                      Exportar Plantilla de Edicion
                    </Dropdown.Item>
                    <Dropdown.Item href='#' onClick={handleShowCargaEdicionExcel}>
                      Cargar Plantilla de edicion
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Stack>
            </Stack>
          </Col>
        </Row>

        <Row className='mt-3'>
          <Col className='d-flex justify-content-between align-items-center'>
            <div className='d-flex gap-2'>
              <span>Leyenda:</span>
              <span>
                <strong>D</strong>: Documentos /
              </span>
              <span>
                <strong>F</strong>: Ficha /
              </span>
              <span>
                <strong>P</strong>: Proceso
              </span>
            </div>
            <div className='d-flex justify-content-end'>
              <p className='d-inline-block m-0 fw-bold border border-dark p-2 rounded-2'>
                {hints} registros
              </p>
            </div>
          </Col>
        </Row>
      </div>
    </>
  )
}

export {PartnersListHeader}
